/**
 * SYSTELOS MONITOR - Página de Configurações
 * Configurações do escritório, notificações e perfil do usuário
 *
 * @version 3.0.0
 * @date 26/01/2026
 * ADD: Seção de configuração de emails de notificação
 * ADD: Botão de teste de email
 * ADD: Múltiplos emails de notificação
 */

import { useState, useEffect } from 'react'
import {
  Settings,
  Building2,
  User,
  Mail,
  Key,
  Loader2,
  Save,
  Bell,
  Clock,
  Plus,
  X,
  Send,
  CheckCircle,
  AlertTriangle,
  Info,
} from 'lucide-react'
import { AppLayout } from '@/components/layout'
import { PageContainer, PageBanner, PageSection } from '@/components/page'
import { useAuth } from '@/contexts/AuthContext'
import authService from '@/services/authService'
import escritorioService from '@/services/escritorioService'
import toast from 'react-hot-toast'

export default function Configuracoes() {
  const { usuario } = useAuth()

  // ============================================================================
  // ESTADOS - NOTIFICAÇÕES
  // ============================================================================
  const [loadingConfig, setLoadingConfig] = useState(true)
  const [salvandoConfig, setSalvandoConfig] = useState(false)
  const [enviandoTeste, setEnviandoTeste] = useState(false)
  const [configAlterada, setConfigAlterada] = useState(false)

  const [formNotificacoes, setFormNotificacoes] = useState({
    email_principal: '',
    emails_extras: [] as string[],
    horario_envio: '08:00',
    notificar_novas: true,
    notificar_vencimentos: true,
    notificar_resumo: true,
  })

  const [novoEmail, setNovoEmail] = useState('')

  // ============================================================================
  // ESTADOS - SENHA
  // ============================================================================
  const [formSenha, setFormSenha] = useState({
    senhaAtual: '',
    novaSenha: '',
    confirmarSenha: '',
  })
  const [salvandoSenha, setSalvandoSenha] = useState(false)

  // ============================================================================
  // CARREGAR CONFIGURAÇÕES
  // ============================================================================
  const carregarConfiguracoes = async () => {
    setLoadingConfig(true)
    try {
      const config = await escritorioService.buscarConfiguracoes()
      
      setFormNotificacoes({
        email_principal: config.email_notificacao || '',
        emails_extras: config.emails_extras || [],
        horario_envio: config.horario_envio || '08:00',
        notificar_novas: config.notificar_novas_comunicacoes ?? true,
        notificar_vencimentos: config.notificar_vencimentos ?? true,
        notificar_resumo: config.notificar_resumo_diario ?? true,
      })
    } catch (error: any) {
      console.error('Erro ao carregar configurações:', error)
      // Se falhar, usar email do usuário como fallback
      if (usuario?.email) {
        setFormNotificacoes(prev => ({
          ...prev,
          email_principal: usuario.email,
        }))
      }
    } finally {
      setLoadingConfig(false)
    }
  }

  useEffect(() => {
    carregarConfiguracoes()
  }, [])

  // ============================================================================
  // HANDLERS - NOTIFICAÇÕES
  // ============================================================================
  const handleChangeNotificacoes = (campo: string, valor: any) => {
    setFormNotificacoes(prev => ({ ...prev, [campo]: valor }))
    setConfigAlterada(true)
  }

  const adicionarEmail = () => {
    if (!novoEmail) return
    
    // Validar email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(novoEmail)) {
      toast.error('Email inválido')
      return
    }

    // Verificar duplicidade
    if (
      novoEmail === formNotificacoes.email_principal ||
      formNotificacoes.emails_extras.includes(novoEmail)
    ) {
      toast.error('Este email já foi adicionado')
      return
    }

    // Limite de 3 emails extras (total 4 com principal)
    if (formNotificacoes.emails_extras.length >= 3) {
      toast.error('Limite de 4 emails atingido')
      return
    }

    setFormNotificacoes(prev => ({
      ...prev,
      emails_extras: [...prev.emails_extras, novoEmail],
    }))
    setNovoEmail('')
    setConfigAlterada(true)
  }

  const removerEmail = (email: string) => {
    setFormNotificacoes(prev => ({
      ...prev,
      emails_extras: prev.emails_extras.filter(e => e !== email),
    }))
    setConfigAlterada(true)
  }

  const salvarConfiguracoes = async () => {
    if (!formNotificacoes.email_principal) {
      toast.error('Informe o email principal de notificação')
      return
    }

    setSalvandoConfig(true)
    try {
      await escritorioService.atualizarConfiguracoes({
        email_notificacao: formNotificacoes.email_principal,
        emails_extras: formNotificacoes.emails_extras,
        horario_envio: formNotificacoes.horario_envio,
        notificar_novas_comunicacoes: formNotificacoes.notificar_novas,
        notificar_vencimentos: formNotificacoes.notificar_vencimentos,
        notificar_resumo_diario: formNotificacoes.notificar_resumo,
      })
      
      toast.success('Configurações salvas com sucesso!')
      setConfigAlterada(false)
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erro ao salvar configurações')
    } finally {
      setSalvandoConfig(false)
    }
  }

  const enviarEmailTeste = async () => {
    if (!formNotificacoes.email_principal) {
      toast.error('Informe o email principal primeiro')
      return
    }

    setEnviandoTeste(true)
    try {
      const result = await escritorioService.enviarEmailTeste(formNotificacoes.email_principal)
      
      if (result.success) {
        toast.success('Email de teste enviado! Verifique sua caixa de entrada.')
      } else {
        toast.error(result.message || 'Falha ao enviar email de teste')
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erro ao enviar email de teste')
    } finally {
      setEnviandoTeste(false)
    }
  }

  // ============================================================================
  // HANDLERS - SENHA
  // ============================================================================
  const handleTrocarSenha = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formSenha.senhaAtual || !formSenha.novaSenha || !formSenha.confirmarSenha) {
      toast.error('Preencha todos os campos')
      return
    }

    if (formSenha.novaSenha !== formSenha.confirmarSenha) {
      toast.error('As senhas não conferem')
      return
    }

    if (formSenha.novaSenha.length < 6) {
      toast.error('A senha deve ter pelo menos 6 caracteres')
      return
    }

    setSalvandoSenha(true)
    try {
      await authService.changePassword({
        senhaAtual: formSenha.senhaAtual,
        novaSenha: formSenha.novaSenha,
      })

      toast.success('Senha alterada com sucesso!')
      setFormSenha({ senhaAtual: '', novaSenha: '', confirmarSenha: '' })
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erro ao alterar senha')
    } finally {
      setSalvandoSenha(false)
    }
  }

  // ============================================================================
  // HELPERS
  // ============================================================================
  const planoBadge = (plano?: string) => {
    const estilos: Record<string, string> = {
      STARTER: 'bg-gray-100 text-gray-800',
      BUSINESS: 'bg-blue-100 text-blue-800',
      ENTERPRISE: 'bg-purple-100 text-purple-800',
    }
    return estilos[plano || 'STARTER'] || estilos.STARTER
  }

  // Lista de horários disponíveis
  const horariosDisponiveis = [
    '06:00', '07:00', '08:00', '09:00', '10:00',
    '11:00', '12:00', '13:00', '14:00', '15:00',
    '16:00', '17:00', '18:00', '19:00', '20:00',
  ]

  // ============================================================================
  // RENDER
  // ============================================================================
  return (
    <AppLayout pagina="Configurações">
      <PageContainer>
        {/* Banner */}
        <PageBanner
          titulo="Configurações"
          subtitulo="Gerencie seu escritório e preferências"
          icon={<Settings className="w-6 h-6" />}
        />

        {/* ================================================================
            SEÇÃO: NOTIFICAÇÕES POR EMAIL
            ================================================================ */}
        <PageSection
          titulo="Notificações por Email"
          subtitulo="Configure os emails que receberão as comunicações do DEC e DJE"
          icon={<Bell className="w-5 h-5" />}
        >
          {loadingConfig ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              <span className="ml-2 text-gray-500">Carregando...</span>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Info Box */}
              <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg">
                <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">Como funciona?</p>
                  <p>
                    Todos os dias às <strong>{formNotificacoes.horario_envio}</strong>, 
                    enviamos um email consolidado com todas as novas comunicações 
                    encontradas na DEC (SEFAZ-SP) e DJE (Tribunais).
                  </p>
                </div>
              </div>

              {/* Email Principal */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Email Principal *
                </label>
                <div className="flex gap-2">
                  <div className="flex-1 relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="email"
                      value={formNotificacoes.email_principal}
                      onChange={(e) => handleChangeNotificacoes('email_principal', e.target.value)}
                      placeholder="email@escritorio.com.br"
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F1B3D] focus:border-transparent"
                    />
                  </div>
                  <button
                    onClick={enviarEmailTeste}
                    disabled={enviandoTeste || !formNotificacoes.email_principal}
                    className="px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm"
                    title="Enviar email de teste"
                  >
                    {enviandoTeste ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                    Testar
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Este email receberá todas as notificações do sistema
                </p>
              </div>

              {/* Emails Extras */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Emails Adicionais (opcional)
                </label>
                
                {/* Lista de emails extras */}
                {formNotificacoes.emails_extras.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {formNotificacoes.emails_extras.map((email) => (
                      <span
                        key={email}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-full text-sm"
                      >
                        <Mail className="w-3.5 h-3.5" />
                        {email}
                        <button
                          onClick={() => removerEmail(email)}
                          className="ml-1 p-0.5 hover:bg-gray-200 rounded-full"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                {/* Input para adicionar */}
                {formNotificacoes.emails_extras.length < 3 && (
                  <div className="flex gap-2">
                    <input
                      type="email"
                      value={novoEmail}
                      onChange={(e) => setNovoEmail(e.target.value)}
                      placeholder="adicionar@email.com"
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F1B3D] focus:border-transparent text-sm"
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), adicionarEmail())}
                    />
                    <button
                      onClick={adicionarEmail}
                      disabled={!novoEmail}
                      className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg disabled:opacity-50"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                )}
                
                <p className="text-xs text-gray-500 mt-1">
                  Máximo de 4 emails no total (1 principal + 3 adicionais)
                </p>
              </div>

              {/* Horário de Envio */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Horário do Email Diário
                </label>
                <div className="flex items-center gap-3">
                  <Clock className="w-4 h-4 text-gray-400" />
                  <select
                    value={formNotificacoes.horario_envio}
                    onChange={(e) => handleChangeNotificacoes('horario_envio', e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F1B3D] focus:border-transparent"
                  >
                    {horariosDisponiveis.map((h) => (
                      <option key={h} value={h}>{h}</option>
                    ))}
                  </select>
                  <span className="text-sm text-gray-500">(Horário de Brasília)</span>
                </div>
              </div>

              {/* Tipos de Notificação */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  O que notificar?
                </label>
                <div className="space-y-3">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formNotificacoes.notificar_novas}
                      onChange={(e) => handleChangeNotificacoes('notificar_novas', e.target.checked)}
                      className="w-4 h-4 rounded border-gray-300 text-[#0F1B3D] focus:ring-[#0F1B3D]"
                    />
                    <span className="text-sm text-gray-700">
                      Novas comunicações (intimações, citações, notificações fiscais)
                    </span>
                  </label>

                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formNotificacoes.notificar_vencimentos}
                      onChange={(e) => handleChangeNotificacoes('notificar_vencimentos', e.target.checked)}
                      className="w-4 h-4 rounded border-gray-300 text-[#0F1B3D] focus:ring-[#0F1B3D]"
                    />
                    <span className="text-sm text-gray-700">
                      Alertas de prazos vencendo (7 dias)
                    </span>
                  </label>

                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formNotificacoes.notificar_resumo}
                      onChange={(e) => handleChangeNotificacoes('notificar_resumo', e.target.checked)}
                      className="w-4 h-4 rounded border-gray-300 text-[#0F1B3D] focus:ring-[#0F1B3D]"
                    />
                    <span className="text-sm text-gray-700">
                      Resumo diário (mesmo sem novidades)
                    </span>
                  </label>
                </div>
              </div>

              {/* Botão Salvar */}
              <div className="flex items-center justify-between pt-4 border-t">
                {configAlterada && (
                  <span className="flex items-center gap-2 text-sm text-amber-600">
                    <AlertTriangle className="w-4 h-4" />
                    Alterações não salvas
                  </span>
                )}
                <button
                  onClick={salvarConfiguracoes}
                  disabled={salvandoConfig || !configAlterada}
                  className="ml-auto px-6 py-2.5 text-sm font-semibold rounded-lg text-[#0F1B3D] flex items-center gap-2 hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: '#FFB800' }}
                >
                  {salvandoConfig ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  Salvar Configurações
                </button>
              </div>
            </div>
          )}
        </PageSection>

        {/* ================================================================
            SEÇÃO: INFORMAÇÕES DO ESCRITÓRIO
            ================================================================ */}
        <PageSection
          titulo="Escritório"
          icon={<Building2 className="w-5 h-5" />}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">
                Nome
              </label>
              <p className="text-gray-900 font-medium">{usuario?.escritorio?.nome || '-'}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">
                Plano
              </label>
              <span className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${planoBadge(usuario?.escritorio?.plano)}`}>
                {usuario?.escritorio?.plano || 'STARTER'}
              </span>
            </div>
          </div>

          <div className="mt-6 p-4 bg-blue-50 rounded-xl">
            <h4 className="font-medium text-blue-900 mb-2">Recursos do Plano</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              {(!usuario?.escritorio?.plano || usuario?.escritorio?.plano === 'STARTER') && (
                <>
                  <li>• Até 5 clientes monitorados</li>
                  <li>• 1 usuário</li>
                  <li>• Notificações por email</li>
                </>
              )}
              {usuario?.escritorio?.plano === 'BUSINESS' && (
                <>
                  <li>• Até 25 clientes monitorados</li>
                  <li>• 5 usuários</li>
                  <li>• Notificações por email e SMS</li>
                </>
              )}
              {usuario?.escritorio?.plano === 'ENTERPRISE' && (
                <>
                  <li>• Clientes ilimitados</li>
                  <li>• Usuários ilimitados</li>
                  <li>• Todos os canais de notificação</li>
                  <li>• API e Webhooks</li>
                </>
              )}
            </ul>
          </div>
        </PageSection>

        {/* ================================================================
            SEÇÃO: PERFIL DO USUÁRIO
            ================================================================ */}
        <PageSection
          titulo="Meu Perfil"
          icon={<User className="w-5 h-5" />}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">
                Nome
              </label>
              <p className="text-gray-900">{usuario?.nome || '-'}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">
                Email
              </label>
              <p className="text-gray-900 flex items-center gap-2">
                <Mail className="w-4 h-4 text-gray-400" />
                {usuario?.email || '-'}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">
                Nível de Acesso
              </label>
              <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-medium bg-[#0F1B3D] text-white">
                {usuario?.nivel || 'OPERADOR'}
              </span>
            </div>
          </div>
        </PageSection>

        {/* ================================================================
            SEÇÃO: ALTERAR SENHA
            ================================================================ */}
        <PageSection
          titulo="Segurança"
          subtitulo="Altere sua senha de acesso"
          icon={<Key className="w-5 h-5" />}
        >
          <form onSubmit={handleTrocarSenha} className="max-w-md space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Senha Atual
              </label>
              <input
                type="password"
                value={formSenha.senhaAtual}
                onChange={(e) => setFormSenha((prev) => ({ ...prev, senhaAtual: e.target.value }))}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F1B3D]"
                disabled={salvandoSenha}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Nova Senha
              </label>
              <input
                type="password"
                value={formSenha.novaSenha}
                onChange={(e) => setFormSenha((prev) => ({ ...prev, novaSenha: e.target.value }))}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F1B3D]"
                disabled={salvandoSenha}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Confirmar Nova Senha
              </label>
              <input
                type="password"
                value={formSenha.confirmarSenha}
                onChange={(e) => setFormSenha((prev) => ({ ...prev, confirmarSenha: e.target.value }))}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F1B3D]"
                disabled={salvandoSenha}
              />
            </div>

            <button
              type="submit"
              disabled={salvandoSenha}
              className="px-6 py-2.5 text-sm font-semibold rounded-lg text-[#0F1B3D] flex items-center gap-2 hover:brightness-110 disabled:opacity-50"
              style={{ backgroundColor: '#FFB800' }}
            >
              {salvandoSenha && <Loader2 className="w-4 h-4 animate-spin" />}
              <Save className="w-4 h-4" />
              Alterar Senha
            </button>
          </form>
        </PageSection>

        {/* ================================================================
            SEÇÃO: SOBRE
            ================================================================ */}
        <PageSection>
          <div className="text-center py-4">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-[#0F1B3D] mb-3">
              <CheckCircle className="w-6 h-6 text-[#FFB800]" />
            </div>
            <h3 className="text-lg font-semibold text-[#0F1B3D] mb-1">SYSTELOS MONITOR</h3>
            <p className="text-sm text-gray-500">Versão 1.0.0</p>
            <p className="text-xs text-gray-400 mt-2">
              © 2026 SYSTELOS - Sistemas com Propósito
            </p>
          </div>
        </PageSection>
      </PageContainer>
    </AppLayout>
  )
}

